import { apiService } from "../../../core/api";
import { API_ENDPOINTS } from "../../../core/constants/endpoints";
import {
  ChatMessage,
  ChatConversation,
  ChatUser,
  ConversationDetailResponse,
  PrivateConversationResponse,
  SendMessagePayload,
  BlockStatus,
  BlockReason,
  BlockUserPayload,
  UserAutocompleteResult,
} from "../types";

/**
 * Get all conversations for the current user, sorted by most recent.
 */
export const getAllConversations = async (
  page = 1,
  search = "",
): Promise<{
  conversations: ChatConversation[];
  hasMore: boolean;
  unreadCount: number;
}> => {
  // API only supports single-term search (first or last name).
  // Send the first word to the API, then filter client-side for remaining words.
  const words = search.trim().split(/\s+/).filter(Boolean);

  let url = `${API_ENDPOINTS.CHAT.GET_ALL}?page=${page}`;
  if (words.length > 0) {
    url += `&search=${encodeURIComponent(words[0])}`;
  }

  const response = await apiService.get<any>(url);

  const unreadCount: number =
    typeof response?.unread_messages_count === "number"
      ? response.unread_messages_count
      : 0;

  const items: any[] = Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
      ? response.data
      : [];

  const conversations: ChatConversation[] = items.map((conv: any) => {
    const lastMsgDate =
      typeof conv.last_message_date === "string"
        ? parseInt(conv.last_message_date, 10)
        : (conv.last_message_date ?? 0);

    // Build a minimal ChatMessage from the list entry if present
    let lastMessage: ChatMessage | null = null;
    if (conv.last_message) {
      if (typeof conv.last_message === "object" && conv.last_message !== null) {
        lastMessage = {
          id: Number(conv.last_message.id),
          conversation_id: Number(
            conv.last_message.conversation_id ??
              conv.id ??
              conv.conversation_id ??
              0,
          ),
          source_uid: Number(conv.last_message.source_uid),
          message: conv.last_message.message,
          created: conv.last_message.created,
        };
      } else {
        lastMessage = {
          id: lastMsgDate,
          conversation_id: Number(conv.id ?? conv.conversation_id ?? 0),
          source_uid: 0,
          message: conv.last_message,
          created: conv.updated ?? "",
        };
      }
    }

    return {
      id: Number(conv.id ?? conv.conversation_id),
      uid: Array.isArray(conv.uid) ? conv.uid : [],
      other_uid: Number(conv.other_uid),
      other_user_avatar: conv.other_user_avatar,
      other_user_name: conv.other_user_name,
      other_user_job: conv.other_user_job,
      last_message: lastMessage,
      last_message_date: lastMsgDate,
      is_read: conv.is_read !== false,
      read_by: Array.isArray(conv.read_by) ? conv.read_by : [],
      updated: conv.updated ?? "",
    } as ChatConversation;
  });

  // Newest first
  conversations.sort(
    (a, b) => (b.last_message_date ?? 0) - (a.last_message_date ?? 0),
  );

  // Client-side filter for additional search words beyond the first
  const extraWords = words.slice(1).map((w) => w.toLowerCase());
  const filtered =
    extraWords.length > 0
      ? conversations.filter((conv) => {
          const name = (conv.other_user_name ?? "").toLowerCase();
          return extraWords.every((word) => name.includes(word));
        })
      : conversations;

  return {
    conversations: filtered,
    hasMore: conversations.length >= 10,
    unreadCount,
  };
};

/**
 * Get messages and user info for a specific conversation.
 */
export const getConversationHistory = async (
  conversationId: string,
  page = 1,
): Promise<{
  messages: ChatMessage[];
  otherUser?: ChatUser;
  blockStatus: BlockStatus | undefined;
  hasMore: boolean;
  page: number;
}> => {
  const url = `${API_ENDPOINTS.CHAT.GET_HISTORY(conversationId)}?page=${page}`;
  const response = await apiService.get<ConversationDetailResponse>(url);

  const convIdNum = parseInt(conversationId, 10);

  const messages: ChatMessage[] = (response.data ?? []).map((msg: any) => ({
    id: Number(msg.id),
    conversation_id: Number(msg.conversation_id ?? convIdNum),
    source_uid: Number(msg.source_uid),
    message: msg.message,
    created: msg.created,
    attachment: msg.attachment ?? null,
  }));

  let otherUser: ChatUser | undefined;
  let blockStatus: BlockStatus | undefined;
  if (response.chat_user?.length) {
    const u = response.chat_user[0];
    otherUser = {
      id: Number(u.other_uid),
      name: u.other_user_name,
      avatar: u.other_user_avatar,
      job: u.other_user_job,
    };
    blockStatus = u.blocked_conversation_status;
  }

  return {
    messages,
    otherUser,
    blockStatus,
    hasMore: messages.length >= 10,
    page,
  };
};

/**
 * Get list of block reasons
 */
export const getBlockReasons = async (): Promise<BlockReason[]> => {
  const response = await apiService.get<any>(API_ENDPOINTS.CHAT.BLOCK_REASONS);
  const items: any[] = Array.isArray(response)
    ? response
    : (response?.data ?? []);
  return items.map((item: any) => ({
    tid: Number(item.tid),
    name: item.name ?? item.label ?? "",
  }));
};

/**
 * Block or unblock a user
 */
export const blockUser = async (payload: BlockUserPayload): Promise<void> => {
  await apiService.post(API_ENDPOINTS.CHAT.BLOCK_USER, payload);
};

/**
 * Get or create a private conversation with a user by their UID.
 * Returns the conversation_id to navigate to.
 */
export const getOrCreatePrivateConversation = async (
  uid: string,
): Promise<number> => {
  const response = await apiService.get<PrivateConversationResponse>(
    API_ENDPOINTS.CHAT.GET_OR_CREATE(uid),
  );
  return (response as any).conversation_id;
};

/**
 * Upload a file attachment for chat
 */
export const uploadChatAttachment = async (file: File): Promise<number> => {
  const url = `${API_ENDPOINTS.FILES.UPLOAD}/chat_message/chat_message/field_attachment?_format=json`;
  const fileBuffer = await file.arrayBuffer();
  const response = await apiService.post<any>(url, fileBuffer, {
    headers: {
      "Content-Type": "application/octet-stream",
      "Content-Disposition": `file; filename="${file.name}"`,
      Accept: "application/json",
    },
  });
  const fid = response?.fid?.[0]?.value;
  if (!fid) throw new Error("No FID received from server");
  return Number(fid);
};

/**
 * Send a message. Optionally attach a file by FID.
 */
export const sendMessage = async (
  conversationId: number,
  message: string,
  fid?: number,
): Promise<ChatMessage | null> => {
  const payload: SendMessagePayload = {
    conversation_id: conversationId,
    message,
  };
  if (fid) payload.fid = fid;
  const response = await apiService.post<any>(
    API_ENDPOINTS.CHAT.POST_MESSAGE,
    payload,
  );

  if (response?.data?.id) {
    const msg = response.data;
    return {
      id: Number(msg.id),
      conversation_id: Number(msg.conversation_id ?? 0),
      source_uid: Number(msg.source_uid),
      message: msg.message,
      created: msg.created,
    };
  }

  return null;
};

/**
 * Mark a conversation as read.
 */
export const markAsRead = async (conversationId: string): Promise<void> => {
  await apiService.patch(API_ENDPOINTS.CHAT.MARK_READ(conversationId), {
    read: true,
  });
};

/**
 * Delete a conversation
 */
export const deleteConversation = async (
  conversationId: string,
): Promise<void> => {
  await apiService.delete(API_ENDPOINTS.CHAT.DELETE(conversationId));
};

/**
 * Search for users by name
 */
export const searchUsers = async (
  query: string,
): Promise<UserAutocompleteResult[]> => {
  const words = query.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const response = await apiService.get<any>(
    API_ENDPOINTS.USER.AUTOCOMPLETE(words[0]),
  );
  const items: any[] = Array.isArray(response)
    ? response
    : (response?.data ?? response?.results ?? []);

  const users = items.map((u: any) => ({
    uid: Number(u.id ?? u.uid),
    firstname: u.firstname,
    lastname: u.lastname,
    logo_uri: u.logo_uri,
    job_title: u.job_title,
  }));

  // Client-side filter for additional search words beyond the first
  const extraWords = words.slice(1).map((w) => w.toLowerCase());
  if (extraWords.length === 0) return users;

  return users.filter((u) => {
    const fullName = `${u.firstname ?? ""} ${u.lastname ?? ""}`.toLowerCase();
    return extraWords.every((word) => fullName.includes(word));
  });
};

// Re-export as chatService object for backwards compatibility
export const chatService = {
  getAllConversations,
  getConversationHistory,
  getBlockReasons,
  blockUser,
  getOrCreatePrivateConversation,
  uploadChatAttachment,
  sendMessage,
  markAsRead,
  deleteConversation,
  searchUsers,
};
